import React, { useState, useEffect, useMemo } from 'react';
import { assetCheckoutAPI, itemsAPI, employeesAPI, locationsAPI, exportAPI } from '../lib/api';
import RequireCap from '../components/auth/RequireCap';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { LogOut, LogIn, Laptop, Search, Loader2, Monitor, Archive, MapPin, Download, Check } from 'lucide-react';
import { formatDate } from '../lib/utils';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { SearchableCombobox } from '../components/SearchableCombobox';
import { EmployeeQuickAddDialog } from '../components/EmployeeQuickAddDialog';

export default function AssetCheckouts() {
  const [checkouts, setCheckouts] = useState([]);
  const [itAssets, setItAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCheckout, setShowCheckout] = useState(false);
  const [showCheckin, setShowCheckin] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedUnitKey, setSelectedUnitKey] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [checkoutNotes, setCheckoutNotes] = useState('');
  const [checkoutReturnDate, setCheckoutReturnDate] = useState('');
  const [checkinForm, setCheckinForm] = useState({ notes: '', condition: 'good', archive: false });
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');
  const [showQuickAddEmployee, setShowQuickAddEmployee] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [coRes, itemsRes, empRes, locRes] = await Promise.all([
        assetCheckoutAPI.list(),
        itemsAPI.listAll({ category: 'it_assets' }),
        employeesAPI.list({ is_active: true }),
        locationsAPI.list({ is_active: true }),
      ]);
      setCheckouts(coRes.data);
      setItAssets(itemsRes.data);
      setEmployees(empRes.data);
      setLocations(locRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const activeCheckouts = checkouts.filter(c => !c.checked_in_at);
  const history = checkouts.filter(c => c.checked_in_at);

  // Build flat list of all available units across all items
  const allUnits = useMemo(() => {
    const units = [];
    for (const item of itAssets) {
      for (const v of (item.variants || [])) {
        const isCheckedOut = activeCheckouts.some(
          c => c.item_id === item.id && c.variant_sku === v.sku
        );
        if (!isCheckedOut) {
          const details = [
            v.serial_number && `SN: ${v.serial_number}`,
            v.asset_tag && `Tag: ${v.asset_tag}`,
            v.service_tag && `ST: ${v.service_tag}`,
            v.phone_number && `Ph: ${v.phone_number}`,
            v.imei && `IMEI: ${v.imei}`,
          ].filter(Boolean).join(', ');

          units.push({
            _key: `${item.id}::${v.sku}`,
            item_id: item.id,
            variant_sku: v.sku,
            item_name: item.name,
            make: item.make || '',
            model: item.model || '',
            asset_type: item.asset_type || '',
            details,
            _searchStr: `${item.name} ${item.make || ''} ${item.model || ''} ${v.sku} ${v.serial_number || ''} ${v.asset_tag || ''} ${v.service_tag || ''}`,
          });
        }
      }
    }
    return units;
  }, [itAssets, activeCheckouts]);

  // Employee list for combobox
  const employeeOptions = useMemo(() =>
    employees.map(emp => ({
      _key: emp.id,
      _searchStr: `${emp.first_name} ${emp.last_name} ${emp.employee_number || ''}`,
      ...emp,
    })),
    [employees]
  );

  // Location list for combobox
  const locationOptions = useMemo(() =>
    locations.map(loc => ({
      _key: loc.id,
      _searchStr: `${loc.name} ${loc.code} ${loc.address || ''}`,
      ...loc,
    })),
    [locations]
  );

  // Helper to format assignment display
  const formatAssignment = (co) => {
    const parts = [];
    if (co.employee_name) parts.push(co.employee_name);
    if (co.location_name) parts.push(co.location_name);
    if (parts.length === 2) return `${parts[0]} @ ${parts[1]}`;
    return parts[0] || '-';
  };

  const totalUnits = itAssets.reduce((sum, a) => sum + (a.variants || []).length, 0);

  const handleCheckout = async () => {
    if (!selectedUnitKey) {
      toast.error('Select a unit to check out');
      return;
    }
    if (!selectedEmployeeId && !selectedLocationId) {
      toast.error('Assign to an employee, a site, or both');
      return;
    }
    const [item_id, variant_sku] = selectedUnitKey.split('::');
    setSubmitting(true);
    try {
      await assetCheckoutAPI.checkout({
        item_id,
        variant_sku,
        employee_id: selectedEmployeeId || null,
        location_id: selectedLocationId || null,
        notes: checkoutNotes,
        expected_return_date: checkoutReturnDate || null,
      });
      toast.success('Asset checked out successfully');
      setShowCheckout(false);
      setSelectedUnitKey('');
      setSelectedEmployeeId('');
      setSelectedLocationId('');
      setCheckoutNotes('');
      setCheckoutReturnDate('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check out asset');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheckin = async () => {
    setSubmitting(true);
    try {
      await assetCheckoutAPI.checkin(showCheckin.id, checkinForm);
      toast.success(checkinForm.archive ? 'Asset checked in and archived' : 'Asset checked in successfully');
      setShowCheckin(null);
      setCheckinForm({ notes: '', condition: 'good', archive: false });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check in asset');
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = (list) => list.filter(c => {
    const matchesSearch = 
      (c.item_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.employee_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.location_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.serial_number || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.asset_tag || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.variant_sku || '').toLowerCase().includes(search.toLowerCase());
    const matchesEmployee = filterEmployee === 'all' || c.employee_id === filterEmployee;
    const matchesLocation = filterLocation === 'all' || c.location_id === filterLocation;
    return matchesSearch && matchesEmployee && matchesLocation;
  });

  return (
    <div className="space-y-6" data-testid="asset-checkouts-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">IT Requisitions</h1>
          <p className="text-slate-500 mt-1">Track assignment of laptops, phones, and IT equipment</p>
        </div>
        <div className="flex gap-3">
          <RequireCap module="assets" action="export">
            <Button variant="outline" onClick={async () => {
              try {
                const res = await exportAPI.checkouts();
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a'); a.href = url; a.download = 'it_checkouts.csv'; a.click();
              } catch { toast.error('Export failed'); }
            }} data-testid="export-checkouts-btn">
              <Download className="w-4 h-4 mr-2" />Export CSV
            </Button>
          </RequireCap>
          <RequireCap module="assets" action="add">
            <Button className="btn-navy" onClick={() => setShowCheckout(true)} data-testid="checkout-asset-btn">
              <LogOut className="w-4 h-4 mr-2" /> Check Out Asset
            </Button>
          </RequireCap>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><Laptop className="w-5 h-5 text-blue-600" /></div>
            <div><p className="text-2xl font-bold" data-testid="stat-total-units">{totalUnits}</p><p className="text-sm text-slate-500">Total IT Units</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center"><LogOut className="w-5 h-5 text-amber-600" /></div>
            <div><p className="text-2xl font-bold text-amber-600" data-testid="stat-checked-out">{activeCheckouts.length}</p><p className="text-sm text-slate-500">Deployed</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center"><Monitor className="w-5 h-5 text-emerald-600" /></div>
            <div><p className="text-2xl font-bold text-emerald-600" data-testid="stat-available">{allUnits.length}</p><p className="text-sm text-slate-500">In Stock</p></div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filters */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search by asset name, employee, location, serial number..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="checkout-search" />
          </div>
          <div className="flex gap-3 flex-wrap">
            <Select value={filterEmployee} onValueChange={setFilterEmployee}>
              <SelectTrigger className="w-[220px]" data-testid="filter-employee">
                <SelectValue placeholder="All Employees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employees</SelectItem>
                {employees.map(emp => (
                  <SelectItem key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterLocation} onValueChange={setFilterLocation}>
              <SelectTrigger className="w-[220px]" data-testid="filter-location">
                <SelectValue placeholder="All Warehouses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Warehouses</SelectItem>
                {locations.map(loc => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(filterEmployee !== 'all' || filterLocation !== 'all') && (
              <Button variant="ghost" size="sm" onClick={() => { setFilterEmployee('all'); setFilterLocation('all'); }} data-testid="clear-filters">Clear Filters</Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="active" className="space-y-4">
        <TabsList>
          <TabsTrigger value="active">Deployed ({activeCheckouts.length})</TabsTrigger>
          <TabsTrigger value="history">History ({history.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="active">
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
              ) : filtered(activeCheckouts).length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Asset</TableHead>
                        <TableHead>Asset Tag</TableHead>
                        <TableHead>Serial #</TableHead>
                        <TableHead>Assigned To</TableHead>
                        <TableHead>Checked Out</TableHead>
                        <TableHead>Expected Return</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered(activeCheckouts).map(co => (
                        <TableRow key={co.id}>
                          <TableCell className="font-medium">{co.item_name}</TableCell>
                          <TableCell><Badge variant="outline" className="font-mono text-xs">{co.asset_tag || '-'}</Badge></TableCell>
                          <TableCell className="font-mono text-sm">{co.serial_number || '-'}</TableCell>
                          <TableCell>
                            <div>
                              {co.employee_name && <p className="font-medium">{co.employee_name}</p>}
                              {co.location_name && (
                                <p className="text-sm text-slate-500 flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />{co.location_name}
                                </p>
                              )}
                              {!co.employee_name && !co.location_name && '-'}
                            </div>
                          </TableCell>
                          <TableCell>{formatDate(co.checked_out_at)}</TableCell>
                          <TableCell>{co.expected_return_date ? formatDate(co.expected_return_date) : '-'}</TableCell>
                          <TableCell>
                            <Button size="sm" variant="outline" onClick={() => { setShowCheckin(co); setCheckinForm({ notes: '', condition: 'good' }); }} data-testid={`checkin-btn-${co.id}`}>
                              <LogIn className="w-3 h-3 mr-1" /> Check In
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500"><LogIn className="w-12 h-12 mx-auto mb-4 text-slate-300" /><p>No assets currently deployed</p></div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardContent className="p-0">
              {filtered(history).length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Asset</TableHead>
                        <TableHead>Employee</TableHead>
                        <TableHead>Checked Out</TableHead>
                        <TableHead>Checked In</TableHead>
                        <TableHead>Condition</TableHead>
                        <TableHead>Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered(history).map(co => (
                        <TableRow key={co.id}>
                          <TableCell className="font-medium">{co.item_name}</TableCell>
                          <TableCell>
                            <div>
                              {co.employee_name && <span>{co.employee_name}</span>}
                              {co.location_name && (
                                <span className="text-sm text-slate-500">{co.employee_name ? ` @ ${co.location_name}` : co.location_name}</span>
                              )}
                              {!co.employee_name && !co.location_name && '-'}
                            </div>
                          </TableCell>
                          <TableCell>{formatDate(co.checked_out_at)}</TableCell>
                          <TableCell>{formatDate(co.checked_in_at)}</TableCell>
                          <TableCell><Badge variant="outline" className="capitalize">{co.condition_in || '-'}</Badge>{co.archived && <Badge variant="destructive" className="ml-1 text-xs" data-testid={`archived-badge-${co.id}`}>Archived</Badge>}</TableCell>
                          <TableCell className="text-sm text-slate-600 max-w-[200px] truncate">{co.notes_in || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500"><p>No check-in/check-out history</p></div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Checkout Dialog */}
      <Dialog open={showCheckout} onOpenChange={(open) => { setShowCheckout(open); if (!open) { setSelectedUnitKey(''); setSelectedEmployeeId(''); setSelectedLocationId(''); setCheckoutNotes(''); setCheckoutReturnDate(''); } }}>
        <DialogContent data-testid="checkout-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>Check Out IT Asset</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Select Unit</Label>
              {allUnits.length > 0 ? (
                <SearchableCombobox
                  items={allUnits}
                  value={selectedUnitKey}
                  onSelect={setSelectedUnitKey}
                  placeholder="Search by name, serial, tag..."
                  searchPlaceholder="Type to search units..."
                  emptyText="No available units found."
                  testId="checkout-unit-select"
                  renderItem={(unit, isSelected) => {
                    if (!unit) return 'Search by name, serial, tag...';
                    return (
                      <div className={isSelected ? '' : 'py-0.5'}>
                        <span className="font-medium">{unit.item_name}</span>
                        {unit.make && <span className="text-xs text-muted-foreground ml-1">({unit.make}{unit.model ? ` ${unit.model}` : ''})</span>}
                        <div className="text-xs text-muted-foreground">{unit.details || unit.variant_sku}</div>
                      </div>
                    );
                  }}
                />
              ) : (
                <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded" data-testid="no-units-message">
                  No available units to check out. Add units to your IT assets in the Asset Catalog.
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label>Assign To Employee</Label>
              <p className="text-xs text-slate-400">Optional — assign to an employee, a site, or both</p>
              <SearchableCombobox
                items={employeeOptions}
                value={selectedEmployeeId}
                onSelect={(val) => setSelectedEmployeeId(val === selectedEmployeeId ? '' : val)}
                placeholder="Search employee..."
                searchPlaceholder="Type name or employee #..."
                emptyText="No employees found."
                testId="checkout-employee-select"
                addNewLabel="Add New Employee"
                onAddNew={() => setShowQuickAddEmployee(true)}
                renderItem={(emp, isSelected) => {
                  if (!emp) return 'Search employee...';
                  return (
                    <div>
                      <span className="font-medium">{emp.first_name} {emp.last_name}</span>
                      {emp.employee_number && <span className="ml-2 text-xs text-muted-foreground">{emp.employee_number}</span>}
                    </div>
                  );
                }}
              />
            </div>
            <div className="space-y-1">
              <Label>Assign To Site / Location</Label>
              <p className="text-xs text-slate-400">Optional — select the site this asset is deployed to</p>
              <SearchableCombobox
                items={locationOptions}
                value={selectedLocationId}
                onSelect={(val) => setSelectedLocationId(val === selectedLocationId ? '' : val)}
                placeholder="Search site / location..."
                searchPlaceholder="Type location name or code..."
                emptyText="No locations found."
                testId="checkout-location-select"
                renderItem={(loc, isSelected) => {
                  if (!loc) return 'Search site / location...';
                  return (
                    <div>
                      <span className="font-medium">{loc.name}</span>
                      {loc.code && <span className="ml-2 text-xs text-muted-foreground">({loc.code})</span>}
                      {loc.address && <div className="text-xs text-muted-foreground">{loc.address}</div>}
                    </div>
                  );
                }}
              />
            </div>
            {!selectedEmployeeId && !selectedLocationId && (
              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">Select at least an employee or a site to assign this asset to.</p>
            )}
            <div className="space-y-2">
              <Label>Expected Return Date (optional)</Label>
              <Input type="date" value={checkoutReturnDate} onChange={(e) => setCheckoutReturnDate(e.target.value)} data-testid="checkout-return-date" />
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea value={checkoutNotes} onChange={(e) => setCheckoutNotes(e.target.value)} placeholder="Any notes about this checkout..." data-testid="checkout-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckout(false)}>Cancel</Button>
            <Button className="btn-navy" onClick={handleCheckout} disabled={submitting || !selectedUnitKey || (!selectedEmployeeId && !selectedLocationId)} data-testid="submit-checkout">
              {submitting ? 'Processing...' : 'Check Out'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Checkin Dialog */}
      <Dialog open={!!showCheckin} onOpenChange={() => setShowCheckin(null)}>
        <DialogContent data-testid="checkin-dialog">
          <DialogHeader><DialogTitle>Check In: {showCheckin?.item_name}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-slate-50 p-3 rounded-lg text-sm space-y-1">
              <p><span className="font-medium">Assigned to:</span> {formatAssignment(showCheckin || {})}</p>
              <p><span className="font-medium">Checked out:</span> {showCheckin ? formatDate(showCheckin.checked_out_at) : ''}</p>
              {showCheckin?.asset_tag && <p><span className="font-medium">Asset Tag:</span> {showCheckin.asset_tag}</p>}
              {showCheckin?.serial_number && <p><span className="font-medium">Serial:</span> {showCheckin.serial_number}</p>}
            </div>
            <div className="space-y-2">
              <Label>Condition</Label>
              <Select value={checkinForm.condition} onValueChange={(v) => setCheckinForm({...checkinForm, condition: v})}>
                <SelectTrigger data-testid="checkin-condition"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="good">Good</SelectItem>
                  <SelectItem value="fair">Fair - Minor Wear</SelectItem>
                  <SelectItem value="damaged">Damaged</SelectItem>
                  <SelectItem value="needs_repair">Needs Repair</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea value={checkinForm.notes} onChange={(e) => setCheckinForm({...checkinForm, notes: e.target.value})} placeholder="Condition notes, damage description..." data-testid="checkin-notes" />
            </div>
            <div
              className={cn(
                "flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                checkinForm.archive ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200 hover:bg-slate-100"
              )}
              onClick={() => setCheckinForm({...checkinForm, archive: !checkinForm.archive})}
              data-testid="checkin-archive-toggle"
            >
              <div className={cn(
                "w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors",
                checkinForm.archive ? "bg-red-600 border-red-600" : "border-slate-300"
              )}>
                {checkinForm.archive && <Check className="w-3 h-3 text-white" />}
              </div>
              <div>
                <p className={cn("font-medium text-sm", checkinForm.archive && "text-red-700")}>
                  <Archive className="w-3.5 h-3.5 inline mr-1" />
                  Archive this unit
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Permanently removes this unit from the catalog. Use when a device is broken, lost, or retired.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckin(null)}>Cancel</Button>
            <Button
              onClick={handleCheckin}
              disabled={submitting}
              variant={checkinForm.archive ? "destructive" : "default"}
              className={checkinForm.archive ? '' : 'btn-navy'}
              data-testid="submit-checkin"
            >
              {submitting ? 'Processing...' : checkinForm.archive ? 'Check In & Archive' : 'Check In'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick-add Employee (Task 2) */}
      <EmployeeQuickAddDialog
        open={showQuickAddEmployee}
        onOpenChange={setShowQuickAddEmployee}
        onCreated={async (emp) => {
          try {
            const res = await employeesAPI.list({ is_active: true });
            setEmployees(res.data);
          } catch (_) { /* no-op */ }
          setSelectedEmployeeId(emp.id);
        }}
      />
    </div>
  );
}
